import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'

interface Tariff { id: string; name: string; bikeType: string; isActive: boolean }
interface Point { id: string; name: string }

interface BikeGroup {
  tariffName: string
  count: number
  codePrefix: string
  startNumber: number
  model: string
  pointName: string
  notes: string
}

interface GeneratedRow {
  code: string
  tariff: string
  model: string
  pointName: string
  notes: string
}

function generateRows(groups: BikeGroup[]): GeneratedRow[] {
  const rows: GeneratedRow[] = []
  for (const g of groups) {
    for (let i = 0; i < g.count; i++) {
      rows.push({
        code: `${g.codePrefix}${g.startNumber + i}`,
        tariff: g.tariffName,
        model: g.model,
        pointName: g.pointName,
        notes: g.notes,
      })
    }
  }
  return rows
}

function rowsToCsv(rows: GeneratedRow[]): string {
  const header = 'code,tariff,model,pointName,notes'
  const lines = rows.map((r) =>
    [r.code, r.tariff, r.model, r.pointName, r.notes]
      .map((v) => (v.includes(',') ? `"${v}"` : v))
      .join(',')
  )
  return [header, ...lines].join('\n')
}

const EMPTY_GROUP: BikeGroup = {
  tariffName: '',
  count: 5,
  codePrefix: '',
  startNumber: 1,
  model: '',
  pointName: '',
  notes: '',
}

export default function OnboardingPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [groups, setGroups] = useState<BikeGroup[]>([])
  const [newGroup, setNewGroup] = useState<BikeGroup>({ ...EMPTY_GROUP })
  const [importResult, setImportResult] = useState<{ created: number; skipped: number; errors: string[] } | null>(null)
  const [groupError, setGroupError] = useState('')

  const { data: tariffsData } = useQuery({ queryKey: ['tariffs'], queryFn: () => api.get('/tariffs').then((r) => r.data) })
  const { data: pointsData } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })

  const tariffs: Tariff[] = (tariffsData?.tariffs ?? []).filter((t: Tariff) => t.isActive)
  const points: Point[] = pointsData?.points ?? []

  const generatedRows = generateRows(groups)

  const importMutation = useMutation({
    mutationFn: (rows: GeneratedRow[]) => api.post('/exports/bikes/import', { rows }),
    onSuccess: (res) => {
      setImportResult(res.data.results)
      qc.invalidateQueries({ queryKey: ['bikes'] })
    },
  })

  const downloadCsv = () => {
    const csv = rowsToCsv(generatedRows)
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'bikes-import.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const addGroup = () => {
    if (!newGroup.tariffName) { setGroupError('Выберите тариф'); return }
    if (!newGroup.codePrefix.trim()) { setGroupError('Укажите префикс кода'); return }
    if (newGroup.count < 1 || newGroup.count > 500) { setGroupError('Количество от 1 до 500'); return }
    setGroupError('')
    setGroups((prev) => [...prev, { ...newGroup, codePrefix: newGroup.codePrefix.trim() }])
    setNewGroup((prev) => ({
      ...EMPTY_GROUP,
      tariffName: prev.tariffName,
      pointName: prev.pointName,
      startNumber: prev.startNumber + prev.count,
    }))
  }

  const removeGroup = (i: number) => setGroups((prev) => prev.filter((_, j) => j !== i))

  return (
    <div className="p-4 md:p-6 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold">Заполнение инвентаря</h1>
        <p className="text-sm text-gray-500 mt-1">
          Опишите группы велосипедов — система сгенерирует CSV или сразу загрузит их в базу.
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex gap-2 mb-6">
        {['Группы инвентаря', 'Предпросмотр и загрузка'].map((label, i) => (
          <div
            key={i}
            className={`flex-1 py-2 px-3 rounded-xl text-center text-xs font-semibold transition-colors ${
              step === i + 1 ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-400'
            }`}
          >
            {i + 1}. {label}
          </div>
        ))}
      </div>

      {/* ── Step 1: Define groups ── */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3">
            <h3 className="font-semibold text-sm text-gray-800">Новая группа</h3>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Тариф *</label>
              {tariffs.length === 0 ? (
                <p className="text-xs text-orange-600 bg-orange-50 rounded-xl px-3 py-2">
                  Нет активных тарифов.{' '}
                  <button onClick={() => navigate('/owner/tariffs')} className="underline">Создать тариф →</button>
                </p>
              ) : (
                <select
                  value={newGroup.tariffName}
                  onChange={(e) => { setNewGroup({ ...newGroup, tariffName: e.target.value }); setGroupError('') }}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm"
                >
                  <option value="">Выберите тариф</option>
                  {tariffs.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Префикс кода *</label>
                <input
                  value={newGroup.codePrefix}
                  onChange={(e) => { setNewGroup({ ...newGroup, codePrefix: e.target.value }); setGroupError('') }}
                  placeholder="напр. B или 1"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Начальный номер</label>
                <input
                  type="number"
                  value={newGroup.startNumber}
                  onChange={(e) => setNewGroup({ ...newGroup, startNumber: parseInt(e.target.value) || 1 })}
                  min={1}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Количество *</label>
              <input
                type="number"
                value={newGroup.count}
                onChange={(e) => setNewGroup({ ...newGroup, count: parseInt(e.target.value) || 1 })}
                min={1}
                max={500}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm"
              />
              {newGroup.count > 0 && newGroup.codePrefix && (
                <p className="text-xs text-gray-400 mt-1">
                  Коды: {newGroup.codePrefix}{newGroup.startNumber} … {newGroup.codePrefix}{newGroup.startNumber + newGroup.count - 1}
                </p>
              )}
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Модель (опционально)</label>
              <input
                value={newGroup.model}
                onChange={(e) => setNewGroup({ ...newGroup, model: e.target.value })}
                placeholder="напр. Trek 820"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm"
              />
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Точка (опционально)</label>
              <select
                value={newGroup.pointName}
                onChange={(e) => setNewGroup({ ...newGroup, pointName: e.target.value })}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm"
              >
                <option value="">Без точки</option>
                {points.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Заметки (опционально)</label>
              <input
                value={newGroup.notes}
                onChange={(e) => setNewGroup({ ...newGroup, notes: e.target.value })}
                placeholder="напр. партия 2024"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm"
              />
            </div>

            {groupError && <p className="text-xs text-red-500">{groupError}</p>}

            <button
              onClick={addGroup}
              disabled={tariffs.length === 0}
              className="w-full bg-brand-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-40"
            >
              + Добавить группу
            </button>
          </div>

          {/* Groups summary */}
          {groups.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl p-4">
              <h3 className="font-semibold text-sm mb-3 text-gray-800">
                Добавлено групп: {groups.length} · Итого единиц: {generatedRows.length}
              </h3>
              <div className="space-y-2">
                {groups.map((g, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2.5 text-sm">
                    <div className="min-w-0">
                      <span className="font-mono font-medium text-xs">
                        {g.codePrefix}{g.startNumber}–{g.codePrefix}{g.startNumber + g.count - 1}
                      </span>
                      <span className="text-gray-500 ml-2 text-xs">{g.tariffName}</span>
                      {g.model && <span className="text-gray-400 ml-2 text-xs">{g.model}</span>}
                      {g.pointName && <span className="text-gray-400 ml-2 text-xs">@ {g.pointName}</span>}
                    </div>
                    <button
                      onClick={() => removeGroup(i)}
                      className="text-red-400 hover:text-red-600 text-xs ml-3 shrink-0"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {groups.length > 0 && (
            <button
              onClick={() => setStep(2)}
              className="w-full bg-brand-600 text-white py-3 rounded-xl text-sm font-semibold"
            >
              Далее: предпросмотр →
            </button>
          )}
        </div>
      )}

      {/* ── Step 2: Preview & import ── */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-4">
            <h3 className="font-semibold text-sm mb-3 text-gray-800">
              Предпросмотр — {generatedRows.length} единиц
            </h3>
            <div className="overflow-x-auto">
              <table className="text-xs w-full">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-100">
                    <th className="text-left py-1.5 pr-4 font-medium">Код</th>
                    <th className="text-left py-1.5 pr-4 font-medium">Тариф</th>
                    <th className="text-left py-1.5 pr-4 font-medium">Модель</th>
                    <th className="text-left py-1.5 font-medium">Точка</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedRows.slice(0, 25).map((r, i) => (
                    <tr key={i} className="border-b border-gray-50">
                      <td className="py-1.5 pr-4 font-mono">{r.code}</td>
                      <td className="py-1.5 pr-4 text-gray-700">{r.tariff}</td>
                      <td className="py-1.5 pr-4 text-gray-400">{r.model || '—'}</td>
                      <td className="py-1.5 text-gray-400">{r.pointName || '—'}</td>
                    </tr>
                  ))}
                  {generatedRows.length > 25 && (
                    <tr>
                      <td colSpan={4} className="py-2 text-center text-gray-400">
                        ... ещё {generatedRows.length - 25} строк
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {importResult && (
            <div className={`border rounded-xl p-3 ${importResult.errors.length > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
              <p className="text-sm font-semibold text-green-800">
                Импорт завершён: +{importResult.created} добавлено, {importResult.skipped} пропущено
              </p>
              {importResult.errors.length > 0 && (
                <div className="mt-2 space-y-1">
                  {importResult.errors.map((e, i) => <p key={i} className="text-xs text-red-600">{e}</p>)}
                </div>
              )}
              {importResult.created > 0 && (
                <button
                  onClick={() => navigate('/owner/bikes')}
                  className="text-xs text-brand-600 mt-2 underline block"
                >
                  Перейти к велосипедам →
                </button>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={downloadCsv}
              className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Скачать CSV
            </button>
            <button
              onClick={() => importMutation.mutate(generatedRows)}
              disabled={importMutation.isPending || importResult?.created === generatedRows.length}
              className="flex-1 bg-brand-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60"
            >
              {importMutation.isPending ? 'Загружаем...' : 'Импортировать сейчас'}
            </button>
          </div>

          <button
            onClick={() => { setStep(1); setImportResult(null); importMutation.reset() }}
            className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            ← Назад к группам
          </button>
        </div>
      )}
    </div>
  )
}
